import React from "react";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";

interface FormData {
  treatmentId: string;
  patientId: string | null;
  userName: string;
  regno: string;
  paymentid: string;
  age: number;
  total: number;
  lastDue: number;
  payable: number;
  discount: number;
  netotal: number;
  due: number;
  card: number;
  cash: number;
  bankTransfer: number;
}

interface PaysProps {
  formData: FormData;
  edit: boolean;
  setformData: (data: FormData) => void;
  closePay: () => void;
  handleSave: (e: any) => void;
}

const Pays: React.FC<PaysProps> = ({
  formData,
  setformData,
  handleSave,
  closePay,
  edit,
}) => {
  const updateFormData = (updates: Partial<FormData>) => {
    const newFormData = { ...formData, ...updates };
    newFormData.netotal = newFormData.payable - newFormData.discount;
    newFormData.due =
      newFormData.netotal -
      newFormData.cash -
      newFormData.card -
      newFormData.bankTransfer;
    setformData(newFormData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    updateFormData({ [name]: parseFloat(value) || 0 });
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.cash !== 0 || formData.card !== 0 || formData.bankTransfer !== 0) {
      handleSave(false);
    } else {
      toast("You Cannot Pay With Zero Amount or empty");
    }
  };

  const onSubmitSms = (e: any) => {
    e.preventDefault();
    if (formData.cash !== 0 || formData.card !== 0 || formData.bankTransfer !== 0) {
      handleSave(true);
    } else {
      toast("You Cannot Pay With Zero Amount or empty");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <PaymentIcon className="text-primary" sx={{ fontSize: 18 }} />
            <h2 className="text-sm font-bold text-slate-800">Payment</h2>
          </div>
          <button
            onClick={closePay}
            className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          {/* Amount Display */}
          <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
            <span className="text-xs font-medium text-slate-600">Amount</span>
            <span className="text-sm font-bold text-slate-800">Rs {formData.payable}</span>
          </div>

          {/* Discount Input */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Discount</label>
            <input
              type="number"
              name="discount"
              value={formData.discount || ""}
              onChange={handleInputChange}
              className="w-full px-3 py-2 rounded-lg border border-gray-600 focus:outline-none transition-all text-xs bg-slate-50"
            />
          </div>

          {/* Net Total Display */}
          <div className="flex items-center justify-between p-2.5 bg-emerald-50 rounded-lg">
            <span className="text-xs font-medium text-emerald-600">Net Total</span>
            <span className="text-sm font-bold text-emerald-700">Rs {formData.netotal}</span>
          </div>

          {/* Payment Methods */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-slate-600 mb-0.5">Card</label>
              <input
                type="number"
                name="card"
                value={formData.card || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 text-xs focus:outline-none bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-600 mb-0.5">Cash</label>
              <input
                type="number"
                name="cash"
                value={formData.cash || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 text-xs focus:outline-none bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-600 mb-0.5">Transfer</label>
              <input
                type="number"
                name="bankTransfer"
                value={formData.bankTransfer || ""}
                onChange={handleInputChange}
                className="w-full px-2 py-1.5 rounded-md border border-gray-600 text-xs focus:outline-none bg-slate-50"
              />
            </div>
          </div>

          {/* Due/Advanced Display */}
          <div className={`flex items-center justify-between p-2.5 rounded-lg ${formData.due > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
            <span className={`text-xs font-medium ${formData.due > 0 ? "text-red-600" : "text-emerald-600"}`}>
              {formData.due > 0 ? "Due" : "Advanced"}
            </span>
            <span className={`text-sm font-bold ${formData.due > 0 ? "text-red-700" : "text-emerald-700"}`}>
              Rs {Math.abs(formData.due)}
            </span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onSubmitSms}
              className="flex-1 px-4 py-2.5 rounded-lg border border-primary text-primary font-medium hover:bg-primary/5 transition-colors text-xs"
            >
              Proceed + SMS
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
            >
              {edit ? "Update" : "Proceed"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Pays;
